/**
 * Portal público de transparencia — rediseñado con tabs.
 *
 * Tabs:
 *  1. Resumen          — KPIs + summary markdown + indicadores manuales
 *  2. Geografía        — Mapa Perú full-width con drill-down dep→prov→dist
 *  3. Inserciones      — Tabla detalle filtrable + export CSV
 *  4. Indicadores      — Grid de KPIs adicionales (audiencia, GRP, reach, etc.)
 *  5. Anexos           — Galería de archivos descargables + runs auto-generados
 *
 * Estilo institucional:
 *  - Rojo institucional #c8102e en top rule + accents
 *  - Brand-600 primario + emerald-500 acciones positivas
 *  - Mobile-first responsive, sidebar de filtros como drawer en mobile
 *  - Touch targets ≥44px, contraste AA, focus rings visibles
 */
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Download, FileText, ExternalLink, AlertCircle, CalendarDays,
  Filter, X, Search, ChevronDown, ChevronRight, MapPin, FileSpreadsheet,
  LayoutDashboard, TrendingUp, Table, Paperclip, Menu, ChevronLeft,
  Megaphone, Tag, Radio, Lock,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import {
  media, setPortalKey, withPortalKey, PortalAuthRequiredError,
  type PublicReportPayload, type PortalFilters,
  type PortalFilterOptions, type PortalSummary, type PortalAdRow,
} from "@/lib/api";
import { PeruAudienceMap } from "@/components/feature/media/peru-audience-map";

export const Route = createFileRoute("/public/reports/$token")({
  component: PublicPortal,
});

type Tab = "resumen" | "publicaciones" | "geografia" | "inserciones" | "indicadores" | "anexos";

const TABS: { id: Tab; label: string; icon: typeof LayoutDashboard }[] = [
  { id: "resumen", label: "Resumen", icon: LayoutDashboard },
  { id: "publicaciones", label: "Publicaciones", icon: Megaphone },
  { id: "geografia", label: "Distribución Geográfica", icon: MapPin },
  { id: "inserciones", label: "Inserciones", icon: Table },
  { id: "indicadores", label: "Indicadores", icon: TrendingUp },
  { id: "anexos", label: "Anexos", icon: Paperclip },
];

function PublicPortal() {
  const { token } = Route.useParams();
  return <PortalApp token={token} />;
}

// Cuerpo del portal reutilizable: la ruta /r/$slug pasa el slug como `token`
// (el backend resuelve slug igual que un token en /api/public/reports/{token}).
export function PortalApp({ token }: { token: string }) {
  const [filters, setFilters] = useState<PortalFilters>({});
  const [activeTab, setActiveTab] = useState<Tab>("resumen");
  const [filtersOpen, setFiltersOpen] = useState(false);

  const portal = useQuery({
    queryKey: ["public-portal", token, filters],
    queryFn: () => media.getPublicPortal(token, filters),
    retry: false,
    placeholderData: (prev) => prev,
  });
  const options = useQuery({
    queryKey: ["public-portal-options", token],
    queryFn: () => media.getPublicFilterOptions(token),
    retry: false,
    staleTime: 5 * 60_000,
  });

  if (portal.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <PortalSkeleton />
      </div>
    );
  }
  // El cliente configuró credenciales → pedir login antes de mostrar datos.
  if (portal.error instanceof PortalAuthRequiredError) {
    return <PortalLoginScreen token={token} info={portal.error.info} />;
  }
  if (portal.error || !portal.data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md p-8 bg-white rounded-xl shadow-lg text-center">
          <AlertCircle size={40} className="mx-auto text-rose-600 mb-3" aria-hidden="true" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Reporte no disponible</h1>
          <p className="text-sm text-slate-600">
            El enlace puede haber expirado o no existe. Contacta a la agencia que te lo compartió.
          </p>
        </div>
      </div>
    );
  }

  return (
    <PortalLayout
      token={token}
      payload={portal.data}
      isFetching={portal.isFetching}
      filters={filters}
      setFilters={setFilters}
      options={options.data ?? null}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      filtersOpen={filtersOpen}
      setFiltersOpen={setFiltersOpen}
    />
  );
}

/**
 * Login del portal cuando el cliente configuró credenciales de acceso.
 * El key emitido (HMAC con TTL) se guarda en sessionStorage y se envía en
 * cada request (header X-Portal-Key / query pk en descargas).
 */
function PortalLoginScreen({
  token, info,
}: {
  token: string;
  info: PortalAuthRequiredError["info"];
}) {
  const qc = useQueryClient();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!password || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await media.publicPortalLogin(token, {
        username: username.trim() || undefined,
        password,
      });
      if (res.key) setPortalKey(token, res.key);
      // Re-dispara todas las queries del portal con el key nuevo.
      await qc.invalidateQueries({ queryKey: ["public-portal", token] });
      await qc.invalidateQueries({ queryKey: ["public-portal-options", token] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Credenciales inválidas");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="h-1 bg-[#c8102e]" role="presentation" />
      <div className="min-h-[calc(100vh-4px)] flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-xl shadow-lg p-8 space-y-4">
          <div className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-slate-100 grid place-items-center mb-3">
              <Lock size={22} className="text-[#c8102e]" aria-hidden="true" />
            </div>
            <h1 className="text-lg font-bold text-slate-900">Portal de Transparencia</h1>
            {(info.client_name || info.title) && (
              <p className="text-sm text-slate-600 mt-1">{info.client_name ?? info.title}</p>
            )}
            <p className="text-xs text-slate-500 mt-2">
              Este portal es de acceso restringido. Ingresa las credenciales que te
              compartió la agencia.
            </p>
          </div>

          {info.needs_username && (
            <div>
              <label htmlFor="portal-user" className="block text-xs font-semibold text-slate-700 mb-1">
                Usuario
              </label>
              <input
                id="portal-user"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full h-11 px-3 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
          )}
          <div>
            <label htmlFor="portal-pass" className="block text-xs font-semibold text-slate-700 mb-1">
              Contraseña
            </label>
            <input
              id="portal-pass"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-11 px-3 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>

          {error && (
            <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" role="alert">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!password || submitting}
            className="w-full h-11 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            {submitting ? "Verificando…" : "Ingresar"}
          </button>
        </form>
      </div>
    </div>
  );
}

function PortalSkeleton() {
  return (
    <div className="w-full max-w-7xl px-6 space-y-4 animate-pulse">
      <div className="h-8 bg-slate-200 rounded w-1/3" />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-20 bg-slate-200 rounded-lg" />
        ))}
      </div>
      <div className="h-64 bg-slate-200 rounded-lg" />
    </div>
  );
}

function PortalLayout({
  token, payload, isFetching, filters, setFilters, options,
  activeTab, setActiveTab, filtersOpen, setFiltersOpen,
}: {
  token: string;
  payload: PublicReportPayload;
  isFetching: boolean;
  filters: PortalFilters;
  setFilters: (f: PortalFilters) => void;
  options: PortalFilterOptions | null;
  activeTab: Tab;
  setActiveTab: (t: Tab) => void;
  filtersOpen: boolean;
  setFiltersOpen: (o: boolean) => void;
}) {
  const { report, agency, client, indicators, assets, runs, initial_summary, filtered_summary } = payload;
  const documents = payload.documents ?? [];
  const isClientPortal = payload.portal?.kind === "client";
  const summary = filtered_summary ?? initial_summary;
  const activeCount = countActiveFilters(filters);
  const isFiltered = activeCount > 0;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Top institutional rule */}
      <div className="h-1 bg-[#c8102e]" role="presentation" />

      {/* Sticky institutional header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-11 border border-slate-300 rounded grid place-items-center bg-gradient-to-br from-slate-50 to-slate-100 text-[8px] font-mono text-slate-500 shrink-0">
              ESCUDO
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#c8102e]">
                {agency.name}
              </div>
              <div className="text-sm sm:text-base font-bold text-slate-900 truncate">
                Portal de Transparencia
              </div>
              {client.name && (
                <div className="text-xs text-slate-500 truncate">{client.name}</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {isFetching && (
              <span className="text-brand-600 font-medium hidden sm:inline" aria-live="polite">
                ⟳ Actualizando…
              </span>
            )}
            {agency.website && (
              <a href={agency.website} target="_blank" rel="noopener noreferrer"
                 className="hidden md:inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 hover:underline">
                {agency.website.replace(/^https?:\/\//, "")} <ExternalLink size={10} />
              </a>
            )}
            <button
              onClick={() => setFiltersOpen(true)}
              className="lg:hidden inline-flex items-center gap-1 h-11 px-3 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 active:scale-95 transition-transform"
              aria-label="Abrir filtros"
            >
              <Filter size={14} aria-hidden="true" />
              Filtros
              {activeCount > 0 && (
                <span className="ml-1 bg-white text-brand-700 rounded-full text-[10px] px-1.5 py-0.5 font-bold">
                  {activeCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Tab nav sticky */}
        <nav className="border-t border-slate-100 bg-white" role="tablist" aria-label="Secciones del portal">
          <div className="max-w-7xl mx-auto px-2 sm:px-6 flex gap-1 overflow-x-auto scrollbar-hide">
            {TABS.map(t => {
              const Icon = t.icon;
              const isActive = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`tab-${t.id}`}
                  onClick={() => setActiveTab(t.id)}
                  className={`relative flex items-center gap-2 px-3 sm:px-4 h-12 text-sm font-semibold whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 ${
                    isActive
                      ? "text-brand-700"
                      : "text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <Icon size={16} aria-hidden="true" />
                  <span>{t.label}</span>
                  {isActive && (
                    <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-brand-600 rounded-full" aria-hidden="true" />
                  )}
                </button>
              );
            })}
          </div>
        </nav>
      </header>

      {/* Cover */}
      {report.cover_image_url && (
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto">
            <img src={report.cover_image_url} alt="" className="w-full max-h-64 object-cover" />
          </div>
        </div>
      )}

      {/* Layout sidebar + main */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* SIDEBAR DESKTOP */}
        <aside className="hidden lg:block">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm sticky top-[124px] max-h-[calc(100vh-140px)] overflow-y-auto">
            <FiltersPanel
              filters={filters} setFilters={setFilters} options={options}
              activeCount={activeCount}
              onClose={() => setFiltersOpen(false)}
              showCloseBtn={false}
            />
          </div>
        </aside>

        {/* SIDEBAR DRAWER MOBILE */}
        {filtersOpen && (
          <>
            <div
              className="lg:hidden fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-40"
              onClick={() => setFiltersOpen(false)}
              aria-hidden="true"
            />
            <aside className="lg:hidden fixed inset-y-0 left-0 z-50 w-80 max-w-[88vw] bg-white shadow-2xl overflow-y-auto"
              role="dialog" aria-label="Filtros">
              <FiltersPanel
                filters={filters} setFilters={setFilters} options={options}
                activeCount={activeCount}
                onClose={() => setFiltersOpen(false)}
                showCloseBtn
              />
            </aside>
          </>
        )}

        {/* MAIN */}
        <main className="space-y-6 min-w-0">
          {/* Title + period */}
          <section className="bg-white border border-slate-200 rounded-xl p-4 sm:p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                {isClientPortal && (
                  <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 mb-1">
                    Portal consolidado del cliente
                  </div>
                )}
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900 leading-tight">
                  {report.name}
                </h1>
                <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-600">
                  {report.period_label && (
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarDays size={14} aria-hidden="true" />
                      {report.period_label}
                    </span>
                  )}
                  {report.published_at && (
                    <span className="text-xs">
                      Publicado: {new Date(report.published_at).toLocaleDateString("es-PE")}
                    </span>
                  )}
                </div>
              </div>
              {client.logo_url && (
                <img src={client.logo_url} alt={client.name ?? ""}
                  className="h-12 w-12 rounded object-contain shrink-0" />
              )}
            </div>
          </section>

          {/* Active filters chips bar */}
          {isFiltered && (
            <ActiveFiltersBar
              filters={filters} setFilters={setFilters} options={options}
              exportUrl={media.publicExportCsvUrl(token, filters)}
            />
          )}

          {/* TAB CONTENT */}
          <div id={`tab-${activeTab}`} role="tabpanel" aria-labelledby={`tab-${activeTab}`}>
            {activeTab === "resumen" && (
              <TabResumen
                report={report} agency={agency}
                summary={summary} indicators={indicators}
                isFiltered={isFiltered}
                comparison={isFiltered ? initial_summary : undefined}
                kindLabels={options?.outlet_kind_labels}
              />
            )}
            {activeTab === "publicaciones" && (
              <TabPublicaciones token={token} filters={filters} />
            )}
            {activeTab === "geografia" && (
              <TabGeografia
                token={token} filters={filters} summary={summary}
              />
            )}
            {activeTab === "inserciones" && (
              <TabInserciones token={token} filters={filters} isFiltered={isFiltered}
                kindLabels={options?.outlet_kind_labels} />
            )}
            {activeTab === "indicadores" && (
              <TabIndicadores indicators={indicators} summary={summary} />
            )}
            {activeTab === "anexos" && (
              <TabAnexos assets={assets} runs={runs} documents={documents} token={token} />
            )}
          </div>

          {/* Footer */}
          <footer className="text-center text-xs text-slate-500 py-6 border-t border-slate-200">
            Portal de transparencia · <strong>{agency.name}</strong>
            {agency.description && ` — ${agency.description}`}
            {report.expires_at && (
              <div className="mt-1">
                Vigente hasta: {new Date(report.expires_at).toLocaleDateString("es-PE")}
              </div>
            )}
          </footer>
        </main>
      </div>
    </div>
  );
}

// ============================================================
// TAB RESUMEN
// ============================================================

function TabResumen({
  report, agency, summary, indicators, isFiltered, comparison, kindLabels,
}: {
  report: PublicReportPayload["report"];
  agency: PublicReportPayload["agency"];
  summary: PortalSummary;
  indicators: PublicReportPayload["indicators"];
  isFiltered: boolean;
  comparison?: PortalSummary;
  kindLabels?: Record<string, string>;
}) {
  return (
    <div className="space-y-6">
      {!isFiltered && report.summary_markdown && (
        <section className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6">
          <SimpleMarkdown text={report.summary_markdown} />
        </section>
      )}

      <KpisGrid summary={summary} comparison={comparison} />

      {/* Charts */}
      {summary.monthly_series.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Inserciones por mes" icon={TrendingUp}>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={summary.monthly_series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="placementsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563EB" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#64748B" />
                <YAxis tick={{ fontSize: 11 }} stroke="#64748B" allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Area type="monotone" dataKey="placements" stroke="#2563EB" strokeWidth={2}
                  fill="url(#placementsGrad)" name="Inserciones" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title={`Inversión por mes (${summary.currency})`}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={summary.monthly_series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#64748B" />
                <YAxis tick={{ fontSize: 11 }} stroke="#64748B" />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  formatter={(v: number) => v.toLocaleString("es-PE")} />
                <Bar dataKey="spending" fill="#10B981" radius={[6, 6, 0, 0]} name="Inversión" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      {/* Indicadores manuales (solo en initial / no filtrado) */}
      {!isFiltered && indicators.length > 0 && (
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
            <TrendingUp size={18} aria-hidden="true" /> Indicadores de alcance
          </h2>
          <IndicatorsGrid indicators={indicators} compact />
        </section>
      )}

      {/* Distribución breakdowns */}
      <section>
        <h2 className="text-lg font-bold text-slate-900 mb-3">Distribución</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <BreakdownCard title="Por estado" data={summary.by_state} colorMap={STATE_COLORS} />
          <BreakdownCard title="Por cobertura" data={summary.by_coverage} />
          <BreakdownCard title="Por tipo de medio" data={summary.by_outlet_kind} labels={kindLabels} />
          <BreakdownCard title="Por tipo de aviso" data={summary.by_ad_type} />
          {Object.keys(summary.by_priority).length > 0 && (
            <BreakdownCard title="Por prioridad" data={summary.by_priority} />
          )}
          {Object.keys(summary.by_category).length > 0 && (
            <BreakdownCard title="Por categoría" data={summary.by_category} />
          )}
        </div>
      </section>
    </div>
  );
}

// ============================================================
// TAB GEOGRAFÍA
// ============================================================

function TabGeografia({
  token, filters, summary,
}: {
  token: string;
  filters: PortalFilters;
  summary: PortalSummary;
}) {
  if (!summary.by_region.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
        <MapPin size={32} className="mx-auto text-slate-400 mb-3" aria-hidden="true" />
        <p className="text-slate-600">No hay datos geográficos para los filtros aplicados.</p>
      </div>
    );
  }
  return (
    <section className="space-y-4">
      <div className="bg-gradient-to-r from-brand-50 to-emerald-50 border border-brand-200 rounded-xl p-4">
        <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <MapPin size={18} aria-hidden="true" /> Distribución geográfica
        </h2>
        <p className="text-sm text-slate-600 mt-1">
          Mapa choropleth con desglose por departamento, provincia y distrito.
          Haz clic en una región para ver el detalle. Geometría INEI.
        </p>
      </div>
      <PeruAudienceMap
        token={token}
        filters={filters}
        initialDep={summary.by_region}
      />
    </section>
  );
}

// ============================================================
// TAB INSERCIONES (tabla)
// ============================================================

function TabInserciones({
  token, filters, isFiltered, kindLabels,
}: {
  token: string;
  filters: PortalFilters;
  isFiltered: boolean;
  kindLabels?: Record<string, string>;
}) {
  return (
    <div className="space-y-4">
      {!isFiltered && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex items-start gap-2">
          <AlertCircle size={16} className="shrink-0 mt-0.5" aria-hidden="true" />
          <div>
            <strong>Vista sin filtros:</strong> mostrando todas las inserciones del período.
            Usa los filtros de la barra lateral para acotar resultados y habilitar el export CSV.
          </div>
        </div>
      )}
      <PlacementsTable token={token} filters={filters} kindLabels={kindLabels} />
    </div>
  );
}

// ============================================================
// TAB PUBLICACIONES (avisos: programados + histórico publicado)
// ============================================================

function TabPublicaciones({ token, filters }: { token: string; filters: PortalFilters }) {
  const [sort, setSort] = useState("recent");
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const q = useQuery({
    queryKey: ["public-ads", token, filters, sort, offset, limit],
    queryFn: () => media.getPublicAds(token, filters, { sort, offset, limit }),
    placeholderData: (prev) => prev,
  });

  if (q.isLoading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">
        Cargando publicaciones…
      </div>
    );
  }
  if (!q.data) return null;

  const items = q.data.items;
  const descartados = items.filter(a => a.status === "descartado");
  const programados = items.filter(a => !a.is_published && a.status !== "descartado");
  const publicados = items.filter(a => a.is_published);
  const totalPages = Math.ceil(q.data.total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-brand-50 to-emerald-50 border border-brand-200 rounded-xl p-4 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Megaphone size={18} aria-hidden="true" /> Publicaciones
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Avisos programados e histórico de los ya publicados. Usa los filtros de la
            barra lateral para acotar por fecha, estado, campaña, reporte y más.
            {" "}<strong>{q.data.total.toLocaleString("es-PE")}</strong> aviso(s).
          </p>
        </div>
        <select value={sort} onChange={(e) => { setSort(e.target.value); setOffset(0); }}
          className="h-10 px-3 text-sm rounded-lg border border-slate-200 bg-white shrink-0"
          aria-label="Ordenar publicaciones">
          <option value="recent">Actividad reciente</option>
          <option value="scheduled_asc">Programación ascendente</option>
          <option value="campaign">Por campaña</option>
        </select>
      </div>

      {!items.length && (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <Megaphone size={32} className="mx-auto text-slate-400 mb-3" aria-hidden="true" />
          <p className="text-slate-600">No hay publicaciones para los filtros aplicados.</p>
        </div>
      )}

      {programados.length > 0 && (
        <AdsSection title="Programados" icon={CalendarDays} count={programados.length} ads={programados} token={token} />
      )}
      {publicados.length > 0 && (
        <AdsSection title="Histórico publicado" icon={Megaphone} count={publicados.length} ads={publicados} token={token} />
      )}
      {descartados.length > 0 && (
        <AdsSection title="Descartados" icon={Megaphone} count={descartados.length} ads={descartados} token={token} />
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-500">Página {currentPage} de {totalPages}</span>
          <div className="flex gap-2">
            <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}
              className="h-10 px-3 rounded-lg border border-slate-200 disabled:opacity-50 hover:bg-slate-50 inline-flex items-center gap-1">
              <ChevronLeft size={14} aria-hidden="true" /> Anterior
            </button>
            <button disabled={offset + limit >= q.data.total} onClick={() => setOffset(offset + limit)}
              className="h-10 px-3 rounded-lg border border-slate-200 disabled:opacity-50 hover:bg-slate-50 inline-flex items-center gap-1">
              Siguiente <ChevronRight size={14} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AdsSection({
  title, icon: Icon, count, ads, token,
}: { title: string; icon: typeof CalendarDays; count: number; ads: PortalAdRow[]; token: string }) {
  return (
    <section>
      <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
        <Icon size={18} aria-hidden="true" /> {title}
        <span className="text-sm text-slate-400 font-normal">· {count}</span>
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {ads.map(a => <AdCard key={a.ad_id} ad={a} token={token} />)}
      </div>
    </section>
  );
}

function AdCard({ ad, token }: { ad: PortalAdRow; token: string }) {
  const dateLabel = ad.is_published
    ? (ad.last_published_at ? `Publicado: ${new Date(ad.last_published_at).toLocaleDateString("es-PE")}` : null)
    : (ad.first_scheduled_at ? `Programado: ${new Date(ad.first_scheduled_at).toLocaleDateString("es-PE")}` : null);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {ad.campaign_name && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-brand-50 text-brand-700 border border-brand-200 px-2 py-0.5 rounded-full mb-1.5">
              <Tag size={10} aria-hidden="true" /> {ad.campaign_name}
            </span>
          )}
          <div className="font-semibold text-slate-900 leading-snug">
            <span className="font-mono text-xs text-slate-500 mr-2">{ad.ad_code}</span>
            {ad.ad_title}
          </div>
          <div className="flex flex-wrap gap-1 mt-1.5">
            <span className="text-[9px] uppercase bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
              {ad.ad_type.replace(/_/g, " ")}
            </span>
            {ad.category && (
              <span className="text-[9px] inline-flex items-center gap-1 bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                {ad.category_color && (
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: ad.category_color }} aria-hidden="true" />
                )}
                {ad.category}
              </span>
            )}
            {ad.priority && ad.priority !== "normal" && (
              <span className="text-[9px] bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">{ad.priority}</span>
            )}
            {ad.tags.map(t => (
              <span key={t} className="text-[9px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">#{t}</span>
            ))}
          </div>
        </div>
        <span className={`shrink-0 text-[11px] px-2 py-0.5 rounded-full font-medium ${adStatusClass(ad.status)}`}>
          {adStatusLabel(ad.status)}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-slate-600">
        {dateLabel && (
          <span className="inline-flex items-center gap-1">
            <CalendarDays size={12} aria-hidden="true" /> {dateLabel}
          </span>
        )}
        <span>{ad.placements_total} inserción{ad.placements_total === 1 ? "" : "es"}</span>
        {ad.total_price > 0 && (
          <span className="font-mono">S/. {ad.total_price.toLocaleString("es-PE", { minimumFractionDigits: 2 })}</span>
        )}
        {ad.outlets.length > 0 && (
          <span className="inline-flex items-center gap-1 min-w-0">
            <Radio size={12} aria-hidden="true" className="shrink-0" />
            <span className="truncate">
              {ad.outlets.slice(0, 3).join(", ")}
              {ad.outlets.length > 3 && ` +${ad.outlets.length - 3}`}
            </span>
          </span>
        )}
      </div>

      {(ad.reports.length > 0 || ad.annexes.length > 0) && (
        <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-2">
          {ad.reports.map(r => (
            r.portal_url ? (
              <a key={`r${r.id}`} href={r.portal_url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-brand-600 text-white hover:bg-brand-700 font-medium">
                <FileText size={12} aria-hidden="true" /> Ver reporte <ExternalLink size={11} aria-hidden="true" />
              </a>
            ) : (
              <span key={`r${r.id}`} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-slate-100 text-slate-600">
                <FileText size={12} aria-hidden="true" /> {r.name}
              </span>
            )
          ))}
          {ad.annexes.map(an => (
            <a key={`a${an.id}`} href={withPortalKey(an.download_url, token)} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:border-brand-500 hover:text-brand-700"
               title={an.file_name ?? an.title}>
              <Download size={12} aria-hidden="true" />
              <span className="truncate max-w-[160px]">{an.title}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

const AD_STATUS_LABELS: Record<PortalAdRow["status"], string> = {
  publicado: "Publicado", programado: "Programado",
  cancelado: "Cancelado", sin_inserciones: "Sin inserciones",
  descartado: "Descartado",
};

function adStatusLabel(s: PortalAdRow["status"]): string {
  return AD_STATUS_LABELS[s] ?? s;
}

function adStatusClass(s: PortalAdRow["status"]): string {
  if (s === "publicado") return "bg-emerald-100 text-emerald-800";
  if (s === "programado") return "bg-amber-100 text-amber-800";
  if (s === "cancelado") return "bg-rose-100 text-rose-800";
  if (s === "descartado") return "bg-slate-200 text-slate-700";
  return "bg-slate-100 text-slate-600";
}

// ============================================================
// TAB INDICADORES
// ============================================================

function TabIndicadores({
  indicators, summary,
}: {
  indicators: PublicReportPayload["indicators"];
  summary: PortalSummary;
}) {
  return (
    <div className="space-y-6">
      <KpisGrid summary={summary} />
      {indicators.length > 0 ? (
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-3">Indicadores externos del reporte</h2>
          <IndicatorsGrid indicators={indicators} />
        </section>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
          <TrendingUp size={32} className="mx-auto text-slate-400 mb-3" aria-hidden="true" />
          <p className="text-slate-600">Este reporte aún no tiene indicadores externos cargados.</p>
        </div>
      )}
    </div>
  );
}

/** Agrupa items por aviso (ad_code). Devuelve [{key, label, items}] con
 *  "General" para los que no tienen aviso vinculado. */
function groupByAd<T extends { ad_code: string | null; ad_title: string | null }>(items: T[]) {
  const order: string[] = [];
  const map = new Map<string, { label: string; items: T[] }>();
  for (const it of items) {
    const key = it.ad_code ?? "__general__";
    if (!map.has(key)) {
      order.push(key);
      map.set(key, {
        label: it.ad_code ? `${it.ad_code}${it.ad_title ? ` — ${it.ad_title}` : ""}` : "General",
        items: [],
      });
    }
    map.get(key)!.items.push(it);
  }
  return order.map(k => ({ key: k, ...map.get(k)! }));
}

function IndicatorsGrid({
  indicators, compact = false,
}: {
  indicators: PublicReportPayload["indicators"];
  compact?: boolean;
}) {
  const groups = groupByAd(indicators);
  const single = groups.length === 1 && groups[0].key === "__general__";
  const gridCls = `grid gap-3 ${compact ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`;

  const card = (ind: PublicReportPayload["indicators"][number]) => (
    <div key={ind.id} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest font-bold text-brand-700 bg-brand-50 px-2 py-0.5 rounded-full">
          {ind.kind}
        </span>
      </div>
      <div className="text-xs text-slate-600 leading-snug mb-2 min-h-[32px]">{ind.label}</div>
      <div className="text-2xl sm:text-3xl font-bold text-slate-900 font-mono">
        {ind.value_numeric != null
          ? Number(ind.value_numeric).toLocaleString("es-PE")
          : ind.value_text ?? "—"}
        {ind.unit && <span className="text-sm text-slate-500 ml-1 font-sans">{ind.unit}</span>}
      </div>
      {ind.source && (
        <div className="text-[10px] text-slate-500 mt-3 pt-2 border-t border-slate-100">
          <strong>Fuente:</strong> {ind.source}
          {ind.measured_at && ` · ${new Date(ind.measured_at).toLocaleDateString("es-PE")}`}
        </div>
      )}
      {ind.notes && <div className="text-xs text-slate-500 mt-2">{ind.notes}</div>}
    </div>
  );

  if (single) return <div className={gridCls}>{groups[0].items.map(card)}</div>;

  return (
    <div className="space-y-5">
      {groups.map(g => (
        <div key={g.key}>
          <div className="text-xs font-bold text-slate-700 mb-2 flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded-full ${g.key === "__general__" ? "bg-slate-100 text-slate-600" : "bg-brand-50 text-brand-700 font-mono"}`}>
              {g.label}
            </span>
            <span className="text-slate-400">· {g.items.length}</span>
          </div>
          <div className={gridCls}>{g.items.map(card)}</div>
        </div>
      ))}
    </div>
  );
}

// ============================================================
// TAB ANEXOS
// ============================================================

function TabAnexos({
  assets, runs, documents, token,
}: {
  assets: PublicReportPayload["assets"];
  runs: PublicReportPayload["runs"];
  documents: NonNullable<PublicReportPayload["documents"]>;
  token: string;
}) {
  if (!assets.length && !runs.length && !documents.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
        <Paperclip size={32} className="mx-auto text-slate-400 mb-3" aria-hidden="true" />
        <p className="text-slate-600">Este portal no tiene anexos descargables.</p>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {documents.length > 0 && (
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
            <FileText size={18} aria-hidden="true" /> Reportes publicados
          </h2>
          <div className="space-y-2">
            {documents.map(d => {
              const inner = (
                <>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center shrink-0">
                      <FileText size={18} className="text-brand-600" aria-hidden="true" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 group-hover:text-brand-700 truncate">{d.name}</div>
                      <div className="text-xs text-slate-500">
                        <span className="uppercase">{d.kind}</span>
                        {d.period_label && ` · ${d.period_label}`}
                        {d.published_at && ` · ${new Date(d.published_at).toLocaleDateString("es-PE")}`}
                      </div>
                    </div>
                  </div>
                  {d.portal_url && <ExternalLink size={16} className="text-slate-400 group-hover:text-brand-600 shrink-0" aria-hidden="true" />}
                </>
              );
              return d.portal_url ? (
                <a key={d.id} href={d.portal_url} target="_blank" rel="noopener noreferrer"
                   className="group flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl p-3 hover:border-brand-500 transition-colors">
                  {inner}
                </a>
              ) : (
                <div key={d.id} className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl p-3">
                  {inner}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {assets.length > 0 && (
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
            <Paperclip size={18} aria-hidden="true" /> Anexos y evidencias
          </h2>
          <div className="space-y-4">
            {groupByAd(assets).map(g => (
              <div key={g.key}>
                {!(groupByAd(assets).length === 1 && g.key === "__general__") && (
                  <div className="text-xs font-bold text-slate-700 mb-2">
                    <span className={`px-2 py-0.5 rounded-full ${g.key === "__general__" ? "bg-slate-100 text-slate-600" : "bg-brand-50 text-brand-700 font-mono"}`}>
                      {g.label}
                    </span>
                    <span className="text-slate-400 ml-2">· {g.items.length}</span>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {g.items.map(a => (
                    <a key={a.id} href={withPortalKey(a.download_url, token)} target="_blank" rel="noopener noreferrer"
                       className="group block bg-white border border-slate-200 rounded-xl p-4 hover:border-brand-500 hover:shadow-md transition-all">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-brand-50 group-hover:bg-brand-100 flex items-center justify-center shrink-0">
                          <FileText size={20} className="text-brand-600" aria-hidden="true" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[10px] uppercase font-mono bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                              {a.file_kind}
                            </span>
                            <span className="text-[10px] uppercase text-slate-500">{a.kind}</span>
                          </div>
                          <div className="font-semibold text-slate-900 group-hover:text-brand-700 truncate">{a.title}</div>
                          {a.description && (
                            <p className="text-xs text-slate-600 mt-1 line-clamp-2">{a.description}</p>
                          )}
                          <div className="text-[10px] text-slate-500 mt-1.5">
                            {a.file_name} {a.file_size && `· ${(a.file_size / 1024 / 1024).toFixed(2)} MB`}
                          </div>
                        </div>
                        <Download size={16} className="text-slate-400 group-hover:text-brand-600 shrink-0 mt-2" aria-hidden="true" />
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {runs.length > 0 && (
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-3">Reportes generados</h2>
          <div className="space-y-2">
            {runs.map(r => (
              <a key={r.id} href={withPortalKey(r.download_url, token)} target="_blank" rel="noopener noreferrer"
                 className="group flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl p-3 hover:border-brand-500 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <FileSpreadsheet size={20} className="text-emerald-600 shrink-0" aria-hidden="true" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 group-hover:text-brand-700">
                      Reporte ejecutado #{r.id}
                    </div>
                    <div className="text-xs text-slate-500">
                      {r.finished_at && new Date(r.finished_at).toLocaleString("es-PE")}
                    </div>
                  </div>
                </div>
                <Download size={16} className="text-slate-400 group-hover:text-brand-600 shrink-0" aria-hidden="true" />
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// ============================================================
// KPIs Grid (reused in Resumen + Indicadores)
// ============================================================

function KpisGrid({ summary, comparison }: { summary: PortalSummary; comparison?: PortalSummary }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
      <Kpi label="Avisos" value={summary.total_ads} comparison={comparison?.total_ads}
        color="from-brand-50 to-brand-100 text-brand-800 border-brand-200" />
      <Kpi label="Inserciones" value={summary.total_placements} comparison={comparison?.total_placements}
        color="from-blue-50 to-blue-100 text-blue-800 border-blue-200" />
      <Kpi label="Cumplimiento"
        value={`${summary.compliance_pct.toFixed(1)}%`}
        comparison={comparison ? `${comparison.compliance_pct.toFixed(1)}%` : undefined}
        color="from-emerald-50 to-emerald-100 text-emerald-800 border-emerald-200" />
      <Kpi label="Realizados" value={summary.by_state.realizado ?? 0}
        comparison={comparison?.by_state.realizado}
        color="from-amber-50 to-amber-100 text-amber-800 border-amber-200" />
      <Kpi label={`Inversión ${summary.currency}`}
        value={summary.total_spending.toLocaleString("es-PE", { maximumFractionDigits: 0 })}
        comparison={comparison ? comparison.total_spending.toLocaleString("es-PE", { maximumFractionDigits: 0 }) : undefined}
        color="from-rose-50 to-rose-100 text-rose-800 border-rose-200" />
    </div>
  );
}

// ============================================================
// Filters Panel
// ============================================================

function FiltersPanel({
  filters, setFilters, options, activeCount, onClose, showCloseBtn,
}: {
  filters: PortalFilters; setFilters: (f: PortalFilters) => void;
  options: PortalFilterOptions | null;
  activeCount: number;
  onClose: () => void;
  showCloseBtn: boolean;
}) {
  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between border-b border-slate-100 pb-3">
        <div className="font-bold flex items-center gap-2">
          <Filter size={16} aria-hidden="true" /> Filtros
          {activeCount > 0 && (
            <span className="text-[10px] bg-brand-600 text-white px-2 py-0.5 rounded-full font-bold">
              {activeCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {activeCount > 0 && (
            <button onClick={() => setFilters({})}
              className="text-xs text-rose-600 hover:text-rose-700 font-medium px-2 py-1">
              Limpiar
            </button>
          )}
          {showCloseBtn && (
            <button onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100"
              aria-label="Cerrar filtros">
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      <FilterGroup label="Búsqueda" defaultOpen>
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            type="search"
            value={filters.q ?? ""}
            onChange={(e) => setFilters({ ...filters, q: e.target.value || undefined })}
            placeholder="Título, código, medio…"
            className="w-full pl-8 h-10 text-sm rounded-lg border border-slate-200 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none"
            aria-label="Buscar"
          />
        </div>
      </FilterGroup>

      <FilterGroup label="Rango de fechas" defaultOpen>
        <div className="space-y-2">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wide text-slate-500 mb-1 block">Desde</span>
            <input type="date" value={filters.date_from ?? ""}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value || undefined })}
              min={options?.date_range.min ?? undefined}
              max={options?.date_range.max ?? undefined}
              className="w-full h-10 px-2 text-sm rounded-lg border border-slate-200" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wide text-slate-500 mb-1 block">Hasta</span>
            <input type="date" value={filters.date_to ?? ""}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value || undefined })}
              min={options?.date_range.min ?? undefined}
              max={options?.date_range.max ?? undefined}
              className="w-full h-10 px-2 text-sm rounded-lg border border-slate-200" />
          </label>
        </div>
      </FilterGroup>

      <FilterGroup label="Estado">
        <MultiTogglesCsv field="state" filters={filters} setFilters={setFilters} options={options?.states ?? []} />
      </FilterGroup>

      <FilterGroup label="Tipo de aviso">
        <MultiTogglesCsv field="ad_type" filters={filters} setFilters={setFilters} options={options?.ad_types ?? []} />
      </FilterGroup>

      <FilterGroup label="Categoría">
        <div className="space-y-1">
          {(options?.categories ?? []).map(c => (
            <MultiToggleCsvId
              key={c.id} field="category_id" id={c.id} label={c.name}
              color={c.color ?? undefined}
              filters={filters} setFilters={setFilters}
            />
          ))}
          {!(options?.categories ?? []).length && <div className="text-[10px] text-slate-400">Sin categorías</div>}
        </div>
      </FilterGroup>

      <FilterGroup label="Campaña">
        <div className="space-y-1 max-h-56 overflow-y-auto">
          {(options?.campaigns ?? []).map(c => (
            <MultiToggleCsvId
              key={c.id} field="campaign_id" id={c.id}
              label={c.name}
              filters={filters} setFilters={setFilters}
            />
          ))}
          {!(options?.campaigns ?? []).length && <div className="text-[10px] text-slate-400">Sin campañas</div>}
        </div>
      </FilterGroup>

      <FilterGroup label="Reporte publicado">
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer min-h-[36px]">
            <input
              type="checkbox" checked={filters.has_report === true}
              onChange={(e) => setFilters({
                ...filters,
                has_report: e.target.checked ? true : (filters.has_report === true ? undefined : filters.has_report),
              })}
              className="w-4 h-4 rounded text-brand-600 focus:ring-2 focus:ring-brand-500"
            />
            Con reporte
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer min-h-[36px]">
            <input
              type="checkbox" checked={filters.has_report === false}
              onChange={(e) => setFilters({
                ...filters,
                has_report: e.target.checked ? false : (filters.has_report === false ? undefined : filters.has_report),
              })}
              className="w-4 h-4 rounded text-brand-600 focus:ring-2 focus:ring-brand-500"
            />
            Sin reporte
          </label>
        </div>
      </FilterGroup>

      <FilterGroup label="Tipo de medio">
        <MultiTogglesCsv field="outlet_kind" filters={filters} setFilters={setFilters}
          options={options?.outlet_kinds ?? []} labels={options?.outlet_kind_labels} />
      </FilterGroup>

      <FilterGroup label="Cobertura">
        <MultiTogglesCsv field="coverage" filters={filters} setFilters={setFilters} options={options?.coverages ?? []} />
      </FilterGroup>

      <FilterGroup label="Prioridad">
        <MultiTogglesCsv field="priority" filters={filters} setFilters={setFilters} options={options?.priorities ?? []} />
      </FilterGroup>

      <FilterGroup label="Etiquetas">
        <MultiTogglesCsv field="tags" filters={filters} setFilters={setFilters} options={options?.ad_tags ?? []} prefix="#" />
      </FilterGroup>

      <FilterGroup label="Evidencias">
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer min-h-[36px]">
            <input
              type="checkbox" checked={filters.has_evidence === true}
              onChange={(e) => setFilters({
                ...filters,
                has_evidence: e.target.checked ? true : (filters.has_evidence === true ? undefined : filters.has_evidence),
              })}
              className="w-4 h-4 rounded text-brand-600 focus:ring-2 focus:ring-brand-500"
            />
            Con evidencia
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer min-h-[36px]">
            <input
              type="checkbox" checked={filters.has_evidence === false}
              onChange={(e) => setFilters({
                ...filters,
                has_evidence: e.target.checked ? false : (filters.has_evidence === false ? undefined : filters.has_evidence),
              })}
              className="w-4 h-4 rounded text-brand-600 focus:ring-2 focus:ring-brand-500"
            />
            Sin evidencia
          </label>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 mt-2 mb-1">Etapas:</div>
            <MultiTogglesCsv field="evidence_stages" filters={filters} setFilters={setFilters}
              options={options?.evidence_stages ?? []} />
          </div>
        </div>
      </FilterGroup>

      <FilterGroup label="Precio (PEN)">
        <div className="grid grid-cols-2 gap-2">
          <input type="number" placeholder="Mín"
            value={filters.price_min ?? ""}
            onChange={(e) => setFilters({ ...filters, price_min: e.target.value ? Number(e.target.value) : undefined })}
            className="h-10 px-2 text-sm rounded-lg border border-slate-200" />
          <input type="number" placeholder="Máx"
            value={filters.price_max ?? ""}
            onChange={(e) => setFilters({ ...filters, price_max: e.target.value ? Number(e.target.value) : undefined })}
            className="h-10 px-2 text-sm rounded-lg border border-slate-200" />
        </div>
        {options?.price_range.min != null && (
          <div className="text-[10px] text-slate-400 mt-1">
            Rango disponible: {options.price_range.min.toFixed(0)} — {options.price_range.max?.toFixed(0)}
          </div>
        )}
      </FilterGroup>

      <FilterGroup label="Región">
        <div className="space-y-0.5 max-h-48 overflow-y-auto">
          {(options?.regions ?? []).map(r => (
            <button
              key={r.id} type="button"
              onClick={() => setFilters({
                ...filters,
                region_id: filters.region_id === r.id ? undefined : r.id,
              })}
              className={`w-full text-left text-sm px-2 py-1.5 rounded transition-colors min-h-[36px] ${
                filters.region_id === r.id
                  ? "bg-brand-100 text-brand-700 font-medium"
                  : "hover:bg-slate-50 text-slate-700"
              }`}
            >
              {r.name}
            </button>
          ))}
        </div>
      </FilterGroup>
    </div>
  );
}

function FilterGroup({
  label, defaultOpen = false, children,
}: { label: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-slate-100 pb-3">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between text-xs font-bold uppercase tracking-widest text-slate-600 mb-2 hover:text-slate-900 min-h-[36px]"
        aria-expanded={open}
      >
        <span>{label}</span>
        {open ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
      </button>
      {open && <div className="space-y-1">{children}</div>}
    </div>
  );
}

function MultiTogglesCsv({
  field, filters, setFilters, options, prefix = "", labels,
}: {
  field: keyof PortalFilters;
  filters: PortalFilters;
  setFilters: (f: PortalFilters) => void;
  options: string[];
  prefix?: string;
  labels?: Record<string, string>;
}) {
  const current = (filters[field] as string | undefined)?.split(",").filter(Boolean) ?? [];
  function toggle(v: string) {
    const next = current.includes(v) ? current.filter(c => c !== v) : [...current, v];
    setFilters({ ...filters, [field]: next.length ? next.join(",") : undefined });
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(o => {
        const on = current.includes(o);
        return (
          <button
            key={o} type="button" onClick={() => toggle(o)}
            className={`px-2.5 py-1 text-xs rounded-full border transition-colors min-h-[28px] ${
              on
                ? "border-brand-500 bg-brand-600 text-white font-medium"
                : "border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300"
            }`}
            aria-pressed={on}
          >
            {prefix}{labels?.[o] ?? o.replace(/_/g, " ")}
          </button>
        );
      })}
      {!options.length && <div className="text-[10px] text-slate-400">Sin opciones</div>}
    </div>
  );
}

function MultiToggleCsvId({
  field, id, label, color, filters, setFilters,
}: {
  field: keyof PortalFilters; id: number; label: string; color?: string;
  filters: PortalFilters; setFilters: (f: PortalFilters) => void;
}) {
  const current = (filters[field] as string | undefined)?.split(",").map(Number).filter(Boolean) ?? [];
  const on = current.includes(id);
  function toggle() {
    const next = on ? current.filter(c => c !== id) : [...current, id];
    setFilters({ ...filters, [field]: next.length ? next.map(String).join(",") : undefined });
  }
  return (
    <button type="button" onClick={toggle}
      className={`w-full flex items-center gap-2 text-left text-sm px-2 py-1.5 rounded transition-colors min-h-[36px] ${
        on ? "bg-brand-100 text-brand-700 font-medium" : "hover:bg-slate-50 text-slate-700"
      }`}
      aria-pressed={on}
    >
      {color && <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} aria-hidden="true" />}
      <span className="truncate">{label}</span>
    </button>
  );
}

// ============================================================
// Active filters chips bar
// ============================================================

function ActiveFiltersBar({
  filters, setFilters, options, exportUrl,
}: {
  filters: PortalFilters; setFilters: (f: PortalFilters) => void;
  options: PortalFilterOptions | null;
  exportUrl: string;
}) {
  const chips = buildChips(filters, options, setFilters);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-2 flex-wrap">
      <div className="text-xs font-bold text-slate-700">Filtros activos:</div>
      {chips.map((chip, i) => (
        <span key={i}
          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 border border-brand-200">
          <span className="text-[9px] uppercase opacity-70">{chip.label}:</span>
          <span className="font-medium">{chip.value}</span>
          <button onClick={chip.onClear}
            className="hover:text-rose-600 ml-0.5 w-4 h-4 flex items-center justify-center"
            aria-label={`Quitar ${chip.label}`}>
            <X size={10} aria-hidden="true" />
          </button>
        </span>
      ))}
      <div className="flex-1" />
      <button onClick={() => setFilters({})}
        className="text-xs text-rose-600 hover:underline font-medium">
        Limpiar todos
      </button>
      <a href={exportUrl} target="_blank" rel="noopener noreferrer"
         className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 font-semibold min-h-[36px]">
        <FileSpreadsheet size={14} aria-hidden="true" /> Exportar CSV
      </a>
    </div>
  );
}

function buildChips(
  filters: PortalFilters, options: PortalFilterOptions | null,
  setFilters: (f: PortalFilters) => void,
) {
  const chips: Array<{ label: string; value: string; onClear: () => void }> = [];
  const clr = (k: keyof PortalFilters) => () => {
    const n = { ...filters }; delete n[k]; setFilters(n);
  };
  if (filters.q) chips.push({ label: "Buscar", value: filters.q, onClear: clr("q") });
  if (filters.date_from) chips.push({ label: "Desde", value: filters.date_from, onClear: clr("date_from") });
  if (filters.date_to) chips.push({ label: "Hasta", value: filters.date_to, onClear: clr("date_to") });
  if (filters.state) chips.push({ label: "Estado", value: filters.state, onClear: clr("state") });
  if (filters.ad_type) chips.push({ label: "Tipo aviso", value: filters.ad_type.replace(/,/g, ", "), onClear: clr("ad_type") });
  if (filters.outlet_kind) chips.push({
    label: "Tipo medio",
    value: filters.outlet_kind.split(",").map(k => options?.outlet_kind_labels?.[k] ?? k).join(", "),
    onClear: clr("outlet_kind"),
  });
  if (filters.coverage) chips.push({ label: "Cobertura", value: filters.coverage, onClear: clr("coverage") });
  if (filters.priority) chips.push({ label: "Prioridad", value: filters.priority, onClear: clr("priority") });
  if (filters.tags) chips.push({ label: "Tags", value: filters.tags, onClear: clr("tags") });
  if (filters.region_id && options) {
    const r = options.regions.find(x => x.id === filters.region_id);
    if (r) chips.push({ label: "Región", value: r.name, onClear: clr("region_id") });
  }
  if (filters.category_id) chips.push({ label: "Categoría", value: filters.category_id, onClear: clr("category_id") });
  if (filters.campaign_id && options) {
    const names = filters.campaign_id.split(",").map(Number)
      .map(id => options.campaigns.find(c => c.id === id)?.name)
      .filter(Boolean);
    if (names.length) chips.push({ label: "Campaña", value: names.join(", "), onClear: clr("campaign_id") });
  }
  if (filters.has_report === true) chips.push({ label: "Reporte", value: "Sí", onClear: clr("has_report") });
  if (filters.has_report === false) chips.push({ label: "Reporte", value: "No", onClear: clr("has_report") });
  if (filters.price_min != null) chips.push({ label: "Min", value: `S/${filters.price_min}`, onClear: clr("price_min") });
  if (filters.price_max != null) chips.push({ label: "Max", value: `S/${filters.price_max}`, onClear: clr("price_max") });
  if (filters.has_evidence === true) chips.push({ label: "Evidencia", value: "Sí", onClear: clr("has_evidence") });
  if (filters.has_evidence === false) chips.push({ label: "Evidencia", value: "No", onClear: clr("has_evidence") });
  if (filters.evidence_stages) chips.push({ label: "Etapas", value: filters.evidence_stages, onClear: clr("evidence_stages") });
  return chips;
}

function countActiveFilters(filters: PortalFilters): number {
  return Object.values(filters).filter(v => v !== undefined && v !== "" && v !== null).length;
}

// ============================================================
// Placements table
// ============================================================

function PlacementsTable({ token, filters, kindLabels }: { token: string; filters: PortalFilters; kindLabels?: Record<string, string> }) {
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState("scheduled_desc");
  const limit = 25;
  const q = useQuery({
    queryKey: ["public-placements", token, filters, offset, sort, limit],
    queryFn: () => media.getPublicPlacements(token, filters, { offset, limit, sort }),
    placeholderData: (prev) => prev,
  });

  if (q.isLoading) return (
    <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">
      Cargando inserciones…
    </div>
  );
  if (!q.data) return null;

  const totalPages = Math.ceil(q.data.total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <section>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-lg font-bold text-slate-900">
          Detalle ({q.data.total.toLocaleString("es-PE")} inserciones)
        </h2>
        <select value={sort} onChange={(e) => { setSort(e.target.value); setOffset(0); }}
          className="h-10 px-3 text-sm rounded-lg border border-slate-200 bg-white">
          <option value="scheduled_desc">Recientes primero</option>
          <option value="scheduled_asc">Antiguos primero</option>
          <option value="price_desc">Precio mayor</option>
          <option value="price_asc">Precio menor</option>
        </select>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase tracking-wide text-slate-600">
              <th scope="col" className="px-3 py-3 font-bold">Código</th>
              <th scope="col" className="px-3 py-3 font-bold">Aviso</th>
              <th scope="col" className="px-3 py-3 font-bold">Medio</th>
              <th scope="col" className="px-3 py-3 font-bold">Cobertura</th>
              <th scope="col" className="px-3 py-3 font-bold">Fecha</th>
              <th scope="col" className="px-3 py-3 font-bold">Estado</th>
              <th scope="col" className="px-3 py-3 font-bold text-right">Precio</th>
              <th scope="col" className="px-3 py-3 font-bold">Evidencia</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {q.data.items.map(p => (
              <tr key={p.id} className="hover:bg-slate-50">
                <td className="px-3 py-2.5 font-mono text-xs">{p.ad_code}</td>
                <td className="px-3 py-2.5">
                  <div className="font-medium truncate max-w-xs">{p.ad_title}</div>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    <span className="text-[9px] uppercase bg-slate-100 px-1.5 py-0.5 rounded">{p.ad_type}</span>
                    {p.category && <span className="text-[9px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">{p.category}</span>}
                    {p.priority && p.priority !== "normal" && <span className="text-[9px] bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded">{p.priority}</span>}
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <div className="truncate max-w-[140px]">{p.outlet_name}</div>
                  <div className="text-[10px] text-slate-500">{kindLabels?.[p.outlet_kind] ?? p.outlet_kind}</div>
                </td>
                <td className="px-3 py-2.5">
                  <div>{p.coverage}</div>
                  {p.region && <div className="text-[10px] text-slate-500">{p.region}</div>}
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap text-xs">
                  {p.scheduled_at ? new Date(p.scheduled_at).toLocaleDateString("es-PE") : "—"}
                </td>
                <td className="px-3 py-2.5">
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${stateClass(p.state)}`}>{p.state}</span>
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">
                  {p.price_amount ? `S/. ${p.price_amount.toLocaleString("es-PE", { minimumFractionDigits: 2 })}` : "—"}
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-0.5 flex-wrap">
                    {Object.entries(p.evidence_counts).map(([stage, count]) => (
                      <span key={stage} title={`${stage}: ${count}`}
                        className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-mono">
                        {stage[0].toUpperCase()}{count}
                      </span>
                    ))}
                  </div>
                  {p.evidences.length > 0 && (
                    <div className="flex flex-col gap-0.5 mt-1">
                      {p.evidences.map(ev => (
                        <a key={ev.id} href={withPortalKey(ev.download_url, token)} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] text-brand-700 hover:underline"
                          title={`${ev.stage} · ${ev.file_name ?? ""}`}>
                          <Download size={10} aria-hidden="true" />
                          <span className="truncate max-w-[120px]">
                            {ev.stage === "facturacion" ? "Factura" : ev.file_name || ev.file_kind}
                          </span>
                        </a>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {!q.data.items.length && (
              <tr><td colSpan={8} className="px-3 py-12 text-center text-slate-400">
                Sin resultados para los filtros aplicados
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3 text-sm">
          <span className="text-slate-500">Página {currentPage} de {totalPages}</span>
          <div className="flex gap-2">
            <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}
              className="h-10 px-3 rounded-lg border border-slate-200 disabled:opacity-50 hover:bg-slate-50 inline-flex items-center gap-1">
              <ChevronLeft size={14} aria-hidden="true" /> Anterior
            </button>
            <button disabled={offset + limit >= q.data.total}
              onClick={() => setOffset(offset + limit)}
              className="h-10 px-3 rounded-lg border border-slate-200 disabled:opacity-50 hover:bg-slate-50 inline-flex items-center gap-1">
              Siguiente <ChevronRight size={14} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

const STATE_COLORS: Record<string, string> = {
  programado: "#F59E0B", finalizado: "#3B82F6", realizado: "#10B981", cancelado: "#EF4444",
};

function stateClass(s: string): string {
  if (s === "realizado") return "bg-emerald-100 text-emerald-800";
  if (s === "finalizado") return "bg-blue-100 text-blue-800";
  if (s === "programado") return "bg-amber-100 text-amber-800";
  if (s === "cancelado") return "bg-rose-100 text-rose-800";
  return "bg-slate-100 text-slate-700";
}

// ============================================================
// Componentes UI atómicos
// ============================================================

function Kpi({
  label, value, comparison, color,
}: {
  label: string; value: string | number; comparison?: string | number; color: string;
}) {
  return (
    <div className={`rounded-xl p-4 bg-gradient-to-br border ${color}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{label}</div>
      <div className="text-2xl sm:text-3xl font-bold mt-1 font-mono tabular-nums">
        {typeof value === "number" ? value.toLocaleString("es-PE") : value}
      </div>
      {comparison !== undefined && (
        <div className="text-[10px] opacity-60 mt-1">
          Total: {typeof comparison === "number" ? comparison.toLocaleString("es-PE") : comparison}
        </div>
      )}
    </div>
  );
}

function ChartCard({
  title, icon: Icon, children,
}: { title: string; icon?: typeof TrendingUp; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
        {Icon && <Icon size={14} className="text-brand-600" aria-hidden="true" />}
        {title}
      </div>
      {children}
    </div>
  );
}

function BreakdownCard({
  title, data, colorMap, labels,
}: { title: string; data: Record<string, number>; colorMap?: Record<string, string>; labels?: Record<string, string> }) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const total = entries.reduce((acc, [, v]) => acc + v, 0);
  if (!entries.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-sm font-bold text-slate-900 mb-3">{title}</div>
      <div className="space-y-2">
        {entries.map(([k, v]) => {
          const pct = total ? Math.round((v / total) * 100) : 0;
          const color = colorMap?.[k] ?? "#2563EB";
          return (
            <div key={k}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="capitalize truncate text-slate-700">{labels?.[k] ?? k.replace(/_/g, " ")}</span>
                <span className="font-mono tabular-nums text-slate-600">{v.toLocaleString("es-PE")} · {pct}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SimpleMarkdown({ text }: { text: string }) {
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];
  let listBuffer: string[] = [];
  const flushList = () => {
    if (listBuffer.length) {
      elements.push(
        <ul key={elements.length} className="list-disc pl-5 space-y-1 my-2 text-sm text-slate-700">
          {listBuffer.map((li, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inline(li) }} />)}
        </ul>
      );
      listBuffer = [];
    }
  };
  function inline(s: string): string {
    return s
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-brand-600 underline">$1</a>');
  }
  for (const raw of lines) {
    const line = raw.trim();
    if (line.startsWith("# ")) { flushList(); elements.push(<h2 key={elements.length} className="text-xl font-bold mt-4 mb-2 text-slate-900">{line.slice(2)}</h2>); }
    else if (line.startsWith("## ")) { flushList(); elements.push(<h3 key={elements.length} className="text-lg font-semibold mt-3 mb-2 text-slate-900">{line.slice(3)}</h3>); }
    else if (line.startsWith("### ")) { flushList(); elements.push(<h4 key={elements.length} className="text-base font-semibold mt-2 mb-1 text-slate-900">{line.slice(4)}</h4>); }
    else if (line.startsWith("- ") || line.startsWith("* ")) { listBuffer.push(line.slice(2)); }
    else if (line === "") { flushList(); }
    else { flushList(); elements.push(<p key={elements.length} className="text-sm leading-relaxed my-2 text-slate-700" dangerouslySetInnerHTML={{ __html: inline(line) }} />); }
  }
  flushList();
  return <div>{elements}</div>;
}
